import React, { useEffect, useState, useCallback } from "react";
import { useOnlineStatus } from "@/lib/useOnlineStatus";
import { getCombinedSnapshot, drainAll, onAnyChange } from "@/lib/syncTruth";
import { Wifi, WifiOff, CloudOff, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

/**
 * SyncBadge — predictable operational state read-out for the field.
 *
 * Shows ONE of four states at a time:
 *   - Synced       (online, queue empty)        ← intentionally quiet
 *   - Pending      (online, queue has items)    ← amber, clickable to sync now
 *   - Syncing      (in-progress)                ← spinner
 *   - Offline      (offline)                    ← red, queue count shown
 *
 * Per Phase 6 direction: rugged + predictable, no noisy mobile-app behavior.
 *
 * Pure display component — all combination/drain logic lives in the
 * canonical `lib/syncTruth.js` (Founder "One Sync Truth" directive), so
 * this badge and the Sheet Viewer's `OperationalStatusSpine` pill can
 * never diverge again. Auto-drain-on-reconnect is driven once, globally,
 * by `<SyncSuccessListener>` (mounted at the App root) — this component
 * only reflects state and offers a manual "sync now" tap.
 */
export default function SyncBadge() {
  const online = useOnlineStatus();
  const [queued, setQueued] = useState(0);
  const [dead, setDead] = useState(0);
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null); // 'ok' | 'partial' | null

  const refresh = useCallback(async () => {
    const snap = await getCombinedSnapshot();
    setQueued(snap.pending);
    setDead(snap.dead || 0);
  }, []);

  useEffect(() => {
    refresh();
    const off = onAnyChange(refresh);
    window.addEventListener("online", refresh);
    window.addEventListener("offline", refresh);
    const iv = setInterval(refresh, 1500);
    return () => {
      off();
      window.removeEventListener("online", refresh);
      window.removeEventListener("offline", refresh);
      clearInterval(iv);
    };
  }, [refresh]);

  const sync = async () => {
    if (busy || !online || queued === 0) return;
    setBusy(true);
    setLastResult(null);
    try {
      const { succeeded, failed } = await drainAll();
      setLastResult(failed > 0 ? "partial" : "ok");
      // Reconciliation is now dispatched centrally by offlineQueueV2's
      // flush() itself (forgedops:offline-synced) so it fires for
      // AUTOMATIC background sync too, not only this manual button —
      // no separate dispatch needed here anymore.
      setTimeout(() => setLastResult(null), 3000);
    } finally {
      setBusy(false);
      refresh();
    }
  };

  // Compute state.
  let state, label, Icon, color, testid;
  if (!online) {
    state = "offline"; label = queued > 0 ? `Offline · ${queued} queued` : "Offline";
    Icon = CloudOff; color = "var(--st-escalated)"; testid = "sync-offline";
  } else if (busy) {
    state = "syncing"; label = "Syncing…"; Icon = Loader2;
    color = "var(--fop-primary)"; testid = "sync-syncing";
  } else if (queued > 0) {
    state = "pending"; label = `${queued} pending · Sync`;
    Icon = WifiOff; color = "var(--st-assigned)"; testid = "sync-pending";
  } else if (dead > 0) {
    // FP-035 (2026-09-09 audit remediation) — dead-lettered work is
    // real, unsynced field work that will NEVER auto-retry. It must
    // never render as "Synced" — that hides a true data-loss risk from
    // the field user. Distinct amber/red "attention" state instead.
    state = "attention_needed"; label = `${dead} need${dead === 1 ? "s" : ""} attention`;
    Icon = AlertTriangle; color = "var(--st-escalated)"; testid = "sync-attention-needed";
  } else if (lastResult === "ok") {
    state = "ok"; label = "Synced"; Icon = CheckCircle2;
    color = "var(--st-corrected)"; testid = "sync-ok";
  } else {
    state = "synced"; label = "Synced"; Icon = Wifi;
    color = "var(--st-corrected)"; testid = "sync-idle";
  }

  return (
    <button
      type="button"
      onClick={sync}
      disabled={!online || busy || queued === 0}
      className={`flex items-center gap-1.5 h-11 px-3.5 rounded-md border text-xs font-mono uppercase tracking-wider transition-colors ${
        state === "synced" || state === "ok"
          ? "border-transparent text-[var(--fop-text-3)] cursor-default"
          : "border-[var(--fop-border)] hover:border-[var(--fop-primary)] cursor-pointer"
      }`}
      style={{ color }}
      title={
        state === "offline"
          ? "You are offline. Pins will queue and sync automatically when you reconnect."
          : state === "pending"
          ? "Click to sync queued pins now."
          : state === "attention_needed"
          ? "Some queued work could not be synced automatically and needs your review (see the offline queue panel)."
          : "All pins synced."
      }
      data-testid={testid}
      data-sync-state={state}
    >
      <Icon size={11} className={busy ? "animate-spin" : ""} />
      <span>{label}</span>
    </button>
  );
}
